import React, { useState } from 'react';
import Button from 'components/UI/V3/Button/Button';
import { Drawer } from 'components/UI/V3/Drawer/Drawer';
import { DrawerHeader } from 'components/UI/V3/Drawer/Header/DrawerHeader';
import { DrawerBody } from 'components/UI/V3/Drawer/Body/DrawerBody';
import { DrawerFooter } from 'components/UI/V3/Drawer/Footer/DrawerFooter';
import { useDrawerContext } from 'components/UI/V3/Drawer/DrawerContext';
import { formatNumber } from '../../../utils';
import CheckIcon from './CheckIcon';
import styles from './Audience.module.scss';

interface TagOption {
    id: string;
    name: string;
    count: number;
}

interface TagsDrawerProps {
    tags: Array<TagOption>;
    selectedIds: Array<string>;
    // eslint-disable-next-line no-unused-vars
    onSave: (ids: Array<string>) => void;
    onClose: () => void;
}

const TagsContent: React.FC<Omit<TagsDrawerProps, 'onClose'>> = ({ tags, selectedIds, onSave }) => {
    const { close } = useDrawerContext();
    const [selected, setSelected] = useState<Array<string>>(selectedIds);

    const allSelected = selected.length === tags.length;
    const toggle = (id: string) =>
        setSelected(prev => (prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]));

    return (
        <>
            <DrawerHeader
                sticky
                title="Add tag(s)"
            />
            <DrawerBody>
                <p className={styles.drawerHint}>Clients with any of these tags receive the email.</p>
                <div className={styles.list}>
                    <button
                        type="button"
                        className={styles.allRow}
                        onClick={() => setSelected(allSelected ? [] : tags.map(tag => tag.id))}
                    >
                        <CheckIcon checked={allSelected} />
                        <span className={styles.rowTitle}>All tags</span>
                    </button>
                    {tags.map(tag => (
                        <button
                            key={tag.id}
                            type="button"
                            className={styles.row}
                            onClick={() => toggle(tag.id)}
                        >
                            <CheckIcon checked={selected.includes(tag.id)} />
                            <span className={styles.rowTitle}>{tag.name}</span>
                            <span className={styles.rowMeta}>
                                {formatNumber(tag.count)} {tag.count === 1 ? 'client' : 'clients'}
                            </span>
                        </button>
                    ))}
                </div>
            </DrawerBody>
            <DrawerFooter>
                <Button
                    variant="outlined"
                    label="Cancel"
                    onClick={close}
                />
                <Button
                    variant="black"
                    label="Save"
                    disabled={selected.length === 0}
                    onClick={() => {
                        onSave(selected);
                        close();
                    }}
                />
            </DrawerFooter>
        </>
    );
};

// Same flow as the marketplace "Add Tag(s)" picker. Tags are shared across the organization.
export const TagsDrawer: React.FC<TagsDrawerProps> = ({ onClose, ...props }) => (
    <Drawer onClose={onClose}>
        <TagsContent {...props} />
    </Drawer>
);

export default TagsDrawer;
